import { BaseTicketComment } from "@mg/schemas/src/christo/catalyst";
import { Prohibit, Robot, Warning } from "@phosphor-icons/react";
import { Avatar } from "@radix-ui/themes";
import cx from "classnames";

import { AvatarWithInitials } from "../../components/AvatarWithInitials";
import { ReactComponent as BrandRobot } from "../../icons/BrandRobot.svg";
import { ReactComponent as CopyRobot } from "../../icons/CopyRobot.svg";
import { ReactComponent as LegalRobot } from "../../icons/LegalRobot.svg";

export function renderPersonaAvatar(persona: string, block = false) {
  switch (persona.toLowerCase()) {
    case "legal":
      return (
        <Avatar
          data-testid="ai-avatar"
          key="legal"
          radius="full"
          size="1"
          variant="solid"
          fallback={<LegalRobot />}
          className={cx(
            "size-6 [&>.rt-AvatarFallback]:bg-teal-teal7 [&>.rt-AvatarFallback]:text-base-black",
            { block },
          )}
        />
      );
    case "copy":
    case "text":
    case "typography":
      return (
        <Avatar
          data-testid="ai-avatar"
          key="copy"
          radius="full"
          size="1"
          variant="solid"
          fallback={<CopyRobot />}
          className={cx(
            "size-6 [&>.rt-AvatarFallback]:bg-puntt-accent-7 [&>.rt-AvatarFallback]:text-base-black",
            { block },
          )}
        />
      );
    case "brand":
      return (
        <Avatar
          data-testid="ai-avatar"
          key="brand"
          radius="full"
          color="amber"
          size="1"
          variant="solid"
          fallback={<BrandRobot />}
          className={cx("size-6", { block })}
        />
      );
    default:
      return (
        <Avatar
          data-testid="ai-avatar"
          key="default"
          radius="full"
          color="amber"
          size="1"
          variant="solid"
          fallback={<Robot />}
          className={cx("size-6", { block })}
        />
      );
  }
}

export function renderCommentAvatar(
  { isAI, aiPersona, createdBy, rule }: BaseTicketComment,
  inline = true,
) {
  if (isAI) {
    const persona = aiPersona?.avatar ? aiPersona.avatar : (rule ?? "");
    return renderPersonaAvatar(persona, !inline);
  }

  if (typeof createdBy === "string") return null;

  return (
    <AvatarWithInitials
      data-testid="user-avatar"
      name={createdBy?.name}
      avatar={createdBy?.avatar}
      size={6}
      className={cx({ flex: !inline })}
    />
  );
}

/**
 * Avatar for artwork review comments.
 * Required comments: Prohibit icon on red background.
 * Non-required comments: Warning icon on gold background.
 */
export function renderReviewCommentAvatar(
  comment: Pick<BaseTicketComment, "isRequired">,
) {
  if (comment.isRequired) {
    return (
      <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-puntt-red-9">
        <Prohibit size={14} weight="fill" className="text-base-white" />
      </div>
    );
  }
  return (
    <div className="flex size-6 shrink-0 items-center justify-center rounded-full bg-puntt-gold-10">
      <Warning size={14} weight="fill" className="text-base-white" />
    </div>
  );
}

export function mapRuleToPersona(rule?: string) {
  rule = rule?.toLowerCase();
  if (rule === "text" || rule === "typography") return "copy";
  if (rule === "brand" || rule === "legal") return rule;
  return "";
}
